import { Button, DatePicker, Select } from "@oysterhr/ui"
import { useTranslation } from "react-i18next"
import { object, string } from "yup"
import { Controller, useForm } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"
import { BenefitsEnrollmentCancellationReasonEnum } from "@oysterhr/schema"
import { useFinishReasonOptions } from "../../utils/useFinishReasonOptions"

export interface FinishReasonFormProps {
  initialValues: FinishReasonFormValues
  onSubmit: (values: FinishReasonFormValues) => void
  onCancel: () => void
}

export interface FinishReasonFormValues {
  enrollmentId: string
  coverageEndDate: string
  cancellationReason: string
}

const { Moved } = BenefitsEnrollmentCancellationReasonEnum

export function FinishReasonForm({ initialValues, onSubmit, onCancel }: FinishReasonFormProps) {
  const { t } = useTranslation("benefits/CancellationReasonForm")

  const validationSchema = object()
    .shape<FinishReasonFormValues>({
      enrollmentId: string(),
      coverageEndDate: string().required(t("coverageEndDate.required")),
      cancellationReason: string().trim().required(t("cancellationReason.required")),
    })
    .defined()

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm<FinishReasonFormValues>({
    resolver: yupResolver(validationSchema),
    defaultValues: initialValues,
  })

  const excludeMoved = initialValues.cancellationReason === Moved
  const cancelationReasons = useFinishReasonOptions(!excludeMoved)

  return (
    <form className="grid gap-4" onSubmit={handleSubmit(onSubmit)}>
      <Controller
        control={control}
        name="coverageEndDate"
        render={({ field: { name, value, onChange }, fieldState: { error } }) => (
          <DatePicker
            className="px-2 placeholder-gray-400"
            label={t("coverageEndDate.label")}
            placeholder={t("coverageEndDate.placeholder")}
            onChange={onChange}
            value={value}
            name={name}
            required
            error={error?.message}
          />
        )}
      />
      <Controller
        control={control}
        name="cancellationReason"
        render={({ field: { value, onChange, name }, fieldState: { error } }) => (
          <Select
            disabled={initialValues.cancellationReason === Moved}
            label={t("cancellationReason.label")}
            placeholder={t("cancellationReason.placeholder")}
            name={name}
            value={value}
            onChange={onChange}
            error={error?.message}
          >
            {cancelationReasons.map(option => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        )}
      />
      <div className="mt-4 flex items-center gap-3 md:justify-end">
        <Button type="button" variation="outline" disabled={isSubmitting} onClick={onCancel}>
          {t("cancelButton")}
        </Button>
        <Button type="submit" isLoading={isSubmitting} disabled={isSubmitting}>
          {t("confirmButton")}
        </Button>
      </div>
    </form>
  )
}
